import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  RouterModule,
  RouterOutlet,
  Router,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { CollectionService } from './core/services/collection.service';
import { ThemeService } from './core/services/theme.service';
import { NavigationService } from './core/services/navigation.service';
import { ScrollService } from './core/services/scroll.service';
import { StockSearchComponent } from './shared/ui/stock-search/stock-search.component';
import { PageHeaderComponent } from './shared/ui/page-header/page-header.component';
import { AuthService } from '@auth0/auth0-angular';
import { UserMenuComponent } from './shared/ui/user-menu/user-menu.component';
import { ToastModule } from 'primeng/toast';
import { environment } from '../environments/environment';
import { Company } from './shared/models/company-model';
import { FeatureAccessDirective } from './shared/directives/feature-access.directive';
import { RolesService } from './core/auth/roles.service';
import { UserStateService } from './core/services/user-state.service';
import { LoggingService } from './core/services/logging.service';
import { PageTitleService } from './core/services/page-title.service';

@Component({
  standalone: true,
  selector: 'app-root',
  imports: [
    CommonModule,
    RouterModule,
    RouterOutlet,
    StockSearchComponent,
    PageHeaderComponent,
    UserMenuComponent,
    ToastModule,
    FeatureAccessDirective,
  ],
  providers: [CollectionService],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  title = environment.site_name;
  currentYear = new Date().getFullYear();

  // Social media links - placeholders for now
  socialLinks = {
    facebook: 'https://facebook.com/wallstyardie',
    twitter: 'https://twitter.com/wallstyardie',
    instagram: 'https://instagram.com/wallstyardie',
    youtube: 'https://youtube.com/@wallstyardie',
    // linkedin: 'https://linkedin.com/company/wallstyardie',
    // podcast: 'https://podcasts.apple.com/wallstyardie'
  };

  // Sidebar collapsed state
  sidebarCollapsed = false;

  // Desktop sidebar collapsed state (reactive signal for desktop/laptop users)
  // Note: State is session-only, resets on page refresh
  desktopSidebarCollapsed = signal<boolean>(false);

  // Mobile sidebar state
  mobileSidebarOpen = false;

  // Active navigation link - now reactive via service
  activeLink = inject(NavigationService).activeLink;
  // Mobile menu state
  mobileMenuOpen = false;

  // Theme service - centralized theme management
  themeService = inject(ThemeService);

  // Roles service - access tier checks
  rolesService = inject(RolesService);
  // Computed signal: true when user has standard access or above
  readonly hasStandardAccess = computed(() => this.rolesService.hasOrExceeds('standard'));
  readonly currentPlan = this.rolesService.highestRole;

  // Inject services first
  private router = inject(Router);
  public auth = inject(AuthService);
  private userStateService = inject(UserStateService);
  private logger = inject(LoggingService);
  private scrollService = inject(ScrollService);
  private pageTitleService = inject(PageTitleService);
  private destroyRef = inject(DestroyRef);
  // Auth0 loading state to prevent page flash (must come after auth injection)
  isLoading = toSignal(this.auth.isLoading$, { initialValue: true });

  // Routes that should not show dashboard layout (auth pages)
  private authRoutes = [
    '/resolving',
    '/pricing',
    '/complete-profile',
    '/accept-terms',
    '/sign-up/verify-email',
    '/error'
  ];

  // Check if current route is an auth route
  isAuthRoute(): boolean {
    return this.authRoutes.some((route) => this.router.url.startsWith(route));
  }

  ngOnInit(): void {
    this.pageTitleService.initialize();

    // Subscribe to router events to scroll to top
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        // Close mobile sidebar on navigation
        this.closeMobileSidebar();
        // If a component has flagged that the next scroll reset should be suppressed, honour that
        if (this.scrollService.suppressNextScrollReset) {
          this.scrollService.suppressNextScrollReset = false;
          return;
        }
        // Scroll to top on route change
        this.scrollService.scrollTo(0);
      });

    // Clear the suppress flag if navigation is cancelled or errors out so it never gets stuck
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationCancel || event instanceof NavigationError),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.scrollService.suppressNextScrollReset = false;
      });
  }

  toggleSidebar() {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  // Toggle desktop sidebar collapse state
  toggleDesktopSidebar(): void {
    this.desktopSidebarCollapsed.set(!this.desktopSidebarCollapsed());
  }

  // Mobile sidebar methods
  toggleMobileSidebar() {
    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  closeMobileSidebar() {
    this.mobileSidebarOpen = false;
  }

  // Delegate theme toggle to the service
  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  onCompanySelected(company: Company) {
    this.logger.debug('company selected from top search');

    this.router.navigate(['/stocks', company.ticker]);
  }

   logout() {
    // Clear user state before logging out
    this.userStateService.clearState();
    this.auth.logout({ logoutParams: { returnTo: window.location.origin } });
  }
}
